import { useState, useEffect } from 'react';
import { cn } from '../../utils/cn';
import { FaWhatsapp } from 'react-icons/fa';

interface WhatsAppButtonProps {
  phoneNumber: string;
  message?: string;
  showAfterScroll?: number;
}

export const WhatsAppButton = ({
  phoneNumber,
  message = 'Hello! I am interested in your services.',
  showAfterScroll = 300,
}: WhatsAppButtonProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > showAfterScroll);
    };

    // Show after delay on initial load
    const timer = setTimeout(() => {
      handleScroll();
    }, 2000);

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [showAfterScroll]);

  const handleClick = () => {
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <button
      onClick={handleClick}
      className={cn(
        'fixed bottom-24 right-6 z-50',
        'w-14 h-14 md:w-16 md:h-16',
        'bg-green-500 hover:bg-green-600',
        'text-white rounded-full',
        'shadow-2xl hover:shadow-green-500/50',
        'transition-all duration-300',
        'flex items-center justify-center',
        'group',
        'animate-glow-pulse',
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'
      )}
      aria-label="Chat on WhatsApp"
    >
      {/* Ping animation ring */}
      <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping group-hover:animate-none" />
      
      {/* Icon */}
      <FaWhatsapp className="relative w-7 h-7 md:w-8 md:h-8 group-hover:scale-110 transition-transform" />
      
      {/* Tooltip */}
      <span className="absolute right-full mr-3 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        Chat with us
      </span>
    </button>
  );
};
